// @flow

import * as React from 'react';
import CONST from 'app/common/lib/Const';
import i18n from 'app/common/lib/i18n';
import passwordResetRequestHandler from 'app/common/react/components/PasswordReset/utils/passwordResetRequestHandler';
import Testable from 'app/common/react/ui/Testable';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { Text } from 'gestalt';

type ACCEPTABLE_ERROR_CODES = 8 | 9 | 19 | 78 | 88 | 429;

const passwordResetButton = (txt: string, userSearch: ?string, handleTouch) => (
  // TODO: Replace with Gestalt touchable when it has an inline prop
  <button
    style={{ background: 'transparent', border: 'none', verticalAlign: 'inherit', padding: 0 }}
    type="button"
    onClick={() => {
      trackRegisterAction('unauth_password_reset_link_tapped');
      // this needs to be lazily executed, otherwise the password reset request will be made on load
      passwordResetRequestHandler({ usernameOrEmail: userSearch, callBack: handleTouch });
    }}
  >
    <Text color="red" inline key="err_text" size="sm" weight="bold">
      {txt}
    </Text>
  </button>
);

type buttonedErrorType = {
  api_error_code: ACCEPTABLE_ERROR_CODES,
  errorMessage: string,
  handleTouch: () => void,
  source: 'emailPasswordStep' | 'login',
  userSearch: ?string,
};

export default ({
  api_error_code,
  errorMessage,
  handleTouch,
  source,
  userSearch,
}: buttonedErrorType) => {
  const buildPasswordResetLink = (str) => passwordResetButton(str, userSearch, handleTouch);

  const texts = ((errorCode: number): Array<string> => {
    switch (errorCode) {
      // RATE LIMITED
      case CONST.API_EVENT_BLOCKED_ERROR: // Error code 9
      case CONST.API_LIMIT_EXCEEDED_ERROR: // Error code 8
      case CONST.RESPONSE_CODE_TOO_MANY_REQUESTS: // http status (passed as error code) 429
        return interpolateNamedTemplate(
          i18n._(
            'You’ve entered a lot of incorrect passwords. To get back into your account, ' +
              '{{ resetPasswordLink }}. Or you can wait 30 minutes and try again.',
            'Error message when login limited',
          ),
          {
            resetPasswordLink: buildPasswordResetLink(
              i18n._('reset your password', 'Link text for password reset'),
            ),
          },
        );
      // BANNED
      case CONST.LOGIN_HARD_BANNED_USER: // Error code 88
      case CONST.USER_IN_SAFE_MODE: // Error code 19
        return interpolateNamedTemplate(
          i18n._(
            'We noticed some strange activity on your account.  {{ resetPasswordLink }} or log in with Facebook ' +
              'or Google to get back into your account.',
            'Error message when user is soft banned',
          ),
          {
            resetPasswordLink: buildPasswordResetLink(
              i18n._('Reset your password', 'Link text for password reset'),
            ),
          },
        );
      // NO PASSWORD BUT HAS GOOGLE
      case CONST.INVALID_PASSWORD_GOOGLE_USER: //Error Code 76
        return interpolateNamedTemplate(
          i18n._(
            'Your account is already linked to Google. To log in with email, {{ resetPasswordLink }}',
          ),
          {
            resetPasswordLink: buildPasswordResetLink(
              i18n._(
                'set a password.',
                'Link text to set password for user with account with only google auth',
              ),
            ),
          },
        );
      // NO PASSWORD BUT HAS FACEBOOK
      case CONST.INVALID_PASSWORD_FB_USER: //Error Code 77
        return interpolateNamedTemplate(
          i18n._(
            'Your account is already linked to Facebook. To log in with email, {{ resetPasswordLink }}',
          ),
          {
            resetPasswordLink: buildPasswordResetLink(
              i18n._(
                'set a password.',
                'Link text to set password for user with account with only facebook auth',
              ),
            ),
          },
        );
      // WRONG PASSWORD
      case CONST.INVALID_PASSWORD_ERROR_CODE: // Error code 78
        return interpolateNamedTemplate(
          i18n._('The password you entered is incorrect. Try again or {{ resetPasswordLink }}'),
          {
            resetPasswordLink: buildPasswordResetLink(
              i18n._('Reset your password', 'Link text for password reset'),
            ),
          },
        );

      default:
        return [errorMessage];
    }
  })(api_error_code);

  return (
    <Testable id="touchableErrorMessage">
      {texts.map((txt, idx) => (
        <Text inline color="red" size="sm" key={`err_message_${idx}`}>
          {txt}
        </Text>
      ))}
    </Testable>
  );
};
